/**
 * Annotation attribute schemas — single source of truth for the frontend.
 *
 * These mirror demo-backend/config/annotation_schemas.json and include
 * frontend-only properties (color, group_visibility, and field light flags) that the
 * backend doesn't need. The frontend renders SegmentationAnnotationForm
 * dynamically from these definitions.
 *
 * Changes to field definitions must be made in both this file and the
 * backend JSON to keep them in sync.
 */

import { GATING_MODE, type GatingMode } from "../types/gating";

// ============================================================================
// Types
// ============================================================================

/**
 * A single annotation attribute value. `string[]` is produced only by
 * `multi_select` fields (e.g. hepatic `lesion_subtype`); all other field types
 * produce a scalar.
 */
export type AnnotationValue = string | boolean | number | string[];

/**
 * Whether a category requires / allows a whole-organ mask alongside its lesions.
 * Mirrors the backend `organ_policy`.
 * - `required` — once ≥1 lesion of this category is saved and no organ mask
 *   exists, "Segment Organ" replaces "Mark Complete" until the organ is saved.
 * - `optional` — the Segment Organ button is offered but never blocks completion.
 * - `none` — no organ mask for this category (no button).
 */
export type OrganPolicy = "required" | "optional" | "none";

export interface AnnotationFieldSchema {
  type: "select" | "multi_select" | "boolean" | "number";
  display_name: string;
  options?: string[];
  required: boolean;
  /** Conditional requirement: field is required when the named field has one of the listed values. */
  required_when?: Record<string, string[]>;
  group?: string;
  /** Unit suffix shown next to a number input, e.g. "mm". */
  unit?: string;
  /**
   * Shown in the default "light" attribute view. Fields without `light` are
   * hidden until the annotator clicks "Full view". Unconditional non-light
   * fields are optional; a non-light field with an active `required_when` is
   * enforced only in full view. Light fields are the quick capture set.
   */
  light?: boolean;
}

export interface SegmentationTypeSchema {
  display_name: string;
  /**
   * Viewport gating mode(s) this lesion type supports. Mirrors the backend
   * `annotation_schemas.json` `gating_modes`. Most types support exactly one;
   * some (lymph_node, pancreatic_lesion) support several so the annotator can
   * switch. A project's active mode is derived from the union across its
   * assigned categories via `availableGatingModes` / `defaultGatingMode` — never
   * hardcoded by project-id prefix. Empty = contributes no layout (e.g. organ).
   */
  gating_modes: GatingMode[];
  /** Whether this category requires / allows / forbids a whole-organ mask. */
  organ_policy: OrganPolicy;
  /** Color hex for annotation list swatches. Falls back to a rotating palette if omitted. */
  color?: string;
  field_groups?: string[];
  /** Groups that are only visible when a specific field has a matching value. */
  group_visibility?: Record<string, { field: string; values: string[] }>;
  /** If true, this schema is kept for backwards compat but hidden from the new annotation selector. */
  hidden?: boolean;
  fields: Record<string, AnnotationFieldSchema>;
}

export type AnnotationSchemaMap = Record<string, SegmentationTypeSchema>;

// ============================================================================
// Renal Lesion Schema (expanded — cystic + solid, grouped)
// ============================================================================

export const RENAL_LESION_SCHEMA: SegmentationTypeSchema = {
  display_name: "Renal Lesion",
  gating_modes: [GATING_MODE.VIEW_DEPENDENT],
  organ_policy: "required",
  color: "#a855f7", // purple
  field_groups: ["General", "Cystic Features (Bosniak)", "Solid Features"],
  group_visibility: {
    "Cystic Features (Bosniak)": { field: "lesion_subtype", values: ["cystic", "mixed"] },
    "Solid Features": { field: "lesion_subtype", values: ["solid", "mixed"] },
  },
  fields: {
    lesion_subtype: {
      type: "select",
      display_name: "Lesion Subtype",
      options: ["cystic", "solid", "mixed"],
      required: true,
      group: "General",
      light: true,
    },
    laterality: {
      type: "select",
      display_name: "Laterality",
      options: ["left", "right"],
      required: true,
      group: "General",
      light: true,
    },
    kidney_location: {
      type: "select",
      display_name: "Kidney Location",
      options: ["upper_pole", "mid_kidney", "lower_pole"],
      required: true,
      group: "General",
      light: true,
    },
    bosniak_class: {
      type: "select",
      display_name: "Bosniak Class",
      options: ["I", "II", "IIF", "III", "IV"],
      required: false,
      required_when: { lesion_subtype: ["cystic", "mixed"] },
      group: "Cystic Features (Bosniak)",
    },
    enhancing_nodule: {
      type: "select",
      display_name: "Enhancing Nodule",
      options: ["none", "present_obtuse_margins", "present_acute_margins", "indeterminate"],
      required: false,
      required_when: { lesion_subtype: ["cystic", "mixed"] },
      group: "Cystic Features (Bosniak)",
    },
    enhancement_degree: {
      type: "select",
      display_name: "Enhancement Degree",
      options: ["hyperenhancing", "isoenhancing", "hypoenhancing"],
      required: false,
      required_when: { lesion_subtype: ["solid", "mixed"] },
      group: "Solid Features",
    },
  },
};

// ============================================================================
// Renal Cyst Schema (legacy — kept for backwards compatibility)
// ============================================================================

export const RENAL_CYST_SCHEMA: SegmentationTypeSchema = {
  display_name: "Renal Cyst",
  gating_modes: [GATING_MODE.VIEW_DEPENDENT],
  organ_policy: "required",
  color: "#c084fc", // lighter purple (legacy)
  hidden: true,
  fields: {
    bosniak_class: {
      type: "select",
      display_name: "Bosniak Class",
      options: ["I", "II", "IIF", "III", "IV"],
      required: true,
      light: true,
    },
    wall_thickness: {
      type: "select",
      display_name: "Wall Thickness",
      options: ["thin_leq_2mm", "minimally_thickened_3mm", "thickened_geq_4mm", "irregular"],
      required: true,
      light: true,
    },
    septa: {
      type: "select",
      display_name: "Septa",
      options: [
        "none",
        "few_thin_1_3",
        "many_thin_geq_4",
        "minimally_thickened_3mm",
        "thickened_irregular_geq_4mm",
      ],
      required: true,
      light: true,
    },
    calcification: {
      type: "select",
      display_name: "Calcification",
      options: ["none", "present", "indeterminate"],
      required: true,
      light: true,
    },
    enhancing_nodule: {
      type: "select",
      display_name: "Enhancing Nodule",
      options: ["none", "present_obtuse_margins", "present_acute_margins", "indeterminate"],
      required: true,
      light: true,
    },
    enhancing_septa: {
      type: "select",
      display_name: "Enhancing Septa",
      options: ["none", "present", "indeterminate"],
      required: true,
      light: true,
    },
    too_small_to_categorize: {
      type: "boolean",
      display_name: "Too Small to Categorize",
      required: false,
      light: true,
    },
  },
};

// ============================================================================
// Pulmonary Nodule Schema (Phase 2 — Fleischner / Lung-RADS)
// ============================================================================

export const PULMONARY_NODULE_SCHEMA: SegmentationTypeSchema = {
  display_name: "Pulmonary Nodule",
  gating_modes: [GATING_MODE.SLICE_DEPENDENT],
  organ_policy: "required",
  color: "#3b82f6", // blue
  fields: {
    lung_rads: {
      type: "select",
      display_name: "Lung-RADS",
      options: ["1", "2", "3", "4A", "4B", "4X"],
      required: false,
    },
    attenuation: {
      type: "select",
      display_name: "Attenuation",
      options: ["solid", "part_solid", "ground_glass"],
      required: false,
    },
    margins: {
      type: "select",
      display_name: "Margins",
      options: ["smooth", "lobulated", "spiculated", "irregular", "ill_defined"],
      required: true,
      light: true,
    },
    lung_lobe: {
      type: "select",
      display_name: "Lung Lobe",
      options: [
        "right_upper_lobe",
        "right_middle_lobe",
        "right_lower_lobe",
        "left_upper_lobe",
        "lingula",
        "left_lower_lobe",
      ],
      required: true,
      light: true,
    },
    calcification: {
      type: "select",
      display_name: "Calcification",
      options: ["none", "benign_pattern", "indeterminate"],
      required: true,
      light: true,
    },
  },
};

// ============================================================================
// Lymph Node Schema (Phase 3 — Morphology + Short Axis)
// ============================================================================

export const LYMPH_NODE_SCHEMA: SegmentationTypeSchema = {
  display_name: "Lymph Node",
  gating_modes: [GATING_MODE.VIEW_DEPENDENT, GATING_MODE.PHASE_DEPENDENT],
  organ_policy: "none",
  color: "#ef4444", // red
  fields: {
    nodal_station: {
      type: "select",
      display_name: "Nodal Station",
      options: [
        "cervical",
        "mediastinal",
        "pulmonary_hilar",
        "axillary",
        "celiac",
        "porta_hepatis",
        "gastrohepatic",
        "peripancreatic",
        "mesenteric",
        "paraaortic",
        "aortocaval",
        "paracaval",
        "retrocrural",
        "renal_hilum",
        "common_iliac",
        "external_iliac",
        "internal_iliac",
        "obturator",
        "presacral",
        "mesorectal",
        "inguinal",
      ],
      required: true,
      light: true,
    },
    morphology: {
      type: "select",
      display_name: "Morphology",
      options: ["oval", "round", "irregular", "conglomerate"],
      required: true,
      light: true,
    },
    necrosis: {
      type: "select",
      display_name: "Necrosis",
      options: ["absent", "present"],
      required: true,
      light: true,
    },
    calcification: {
      type: "select",
      display_name: "Calcification",
      options: ["none", "present"],
      required: false,
      light: true,
    },
    laterality: {
      type: "select",
      display_name: "Laterality",
      options: ["left", "right", "midline", "bilateral"],
      required: true,
      light: true,
    },
  },
};

// ============================================================================
// Renal Stone Schema (Task 2 — size-based management)
// ============================================================================

export const RENAL_STONE_SCHEMA: SegmentationTypeSchema = {
  display_name: "Urinary Stone",
  gating_modes: [GATING_MODE.VIEW_DEPENDENT],
  organ_policy: "optional",
  color: "#f59e0b", // amber
  fields: {
    laterality: {
      type: "select",
      display_name: "Laterality",
      options: ["left", "right"],
      required: true,
      light: true,
    },
    location: {
      type: "select",
      display_name: "Location",
      options: [
        "upper_pole_calyx",
        "interpolar_calyx",
        "lower_pole_calyx",
        "renal_pelvis",
        "upj",
        "proximal_ureter",
        "mid_ureter",
        "distal_ureter",
        "uvj",
        "bladder",
      ],
      required: true,
      light: true,
    },
    obstruction: {
      type: "select",
      display_name: "Obstruction",
      options: ["none", "mild_hydronephrosis", "moderate_hydronephrosis", "severe_hydronephrosis"],
      required: true,
      light: true,
    },
    shape: {
      type: "select",
      display_name: "Shape",
      options: ["round_oval", "irregular", "staghorn"],
      required: true,
      light: true,
    },
    perinephric_stranding: {
      type: "select",
      display_name: "Perinephric/Perirenal Stranding",
      options: ["absent", "present"],
      required: false,
      required_when: {
        location: ["proximal_ureter", "mid_ureter", "distal_ureter", "uvj"],
      },
    },
    ureteral_wall_thickening: {
      type: "select",
      display_name: "Ureteral Wall Thickening",
      options: ["absent", "present"],
      required: false,
    },
  },
};

// ============================================================================
// Hepatic Lesion Schema (Task 3 — Subtype-driven CT + conditional LI-RADS)
// ============================================================================

export const HEPATIC_LESION_SCHEMA: SegmentationTypeSchema = {
  display_name: "Hepatic Lesion",
  gating_modes: [GATING_MODE.PHASE_DEPENDENT],
  organ_policy: "required",
  color: "#10b981", // emerald
  field_groups: ["General", "CT Enhancement (Non-Cyst)", "LI-RADS (Cirrhotic Liver)"],
  group_visibility: {
    "LI-RADS (Cirrhotic Liver)": { field: "cirrhotic_liver", values: ["yes"] },
  },
  fields: {
    lesion_subtype: {
      type: "multi_select",
      display_name: "Lesion Subtype",
      options: [
        "cyst",
        "hemangioma",
        "focal_nodular_hyperplasia",
        "hepatic_adenoma",
        "metastasis",
        "hcc",
        "cholangiocarcinoma",
        "other",
        "indeterminate",
      ],
      required: false,
      group: "General",
    },
    liver_segment: {
      type: "select",
      display_name: "Liver Segment (Couinaud)",
      options: [
        "segment_1",
        "segment_2",
        "segment_3",
        "segment_4a",
        "segment_4b",
        "segment_5",
        "segment_6",
        "segment_7",
        "segment_8",
      ],
      required: false,
      group: "General",
    },
    cirrhotic_liver: {
      type: "select",
      display_name: "Cirrhotic Liver",
      options: ["yes", "no", "indeterminate"],
      required: false,
      group: "General",
    },
    arterial_phase_pattern: {
      type: "select",
      display_name: "Arterial Phase Pattern",
      options: [
        "none",
        "flash_filling",
        "peripheral_discontinuous_nodular",
        "homogeneous",
        "rim_continuous",
        "heterogeneous",
        "phase_unavailable",
      ],
      required: false,
      group: "CT Enhancement (Non-Cyst)",
    },
    portal_venous_behavior: {
      type: "select",
      display_name: "Portal Venous Phase Behavior",
      options: [
        "progressive_fill_in",
        "isointense_equilibration",
        "washout",
        "persistent_enhancement",
        "hypoenhancing",
        "phase_unavailable",
      ],
      required: false,
      group: "CT Enhancement (Non-Cyst)",
    },
    li_rads: {
      type: "select",
      display_name: "LI-RADS Category",
      options: ["LR_1", "LR_2", "LR_3", "LR_4", "LR_5", "LR_M", "LR_TIV", "not_applicable"],
      required: false,
      required_when: { cirrhotic_liver: ["yes"] },
      group: "LI-RADS (Cirrhotic Liver)",
    },
  },
};

// ============================================================================
// Pancreatic Lesion Schema (cancer-seg — clinical review 2026-06-23)
// ============================================================================

export const PANCREATIC_LESION_SCHEMA: SegmentationTypeSchema = {
  display_name: "Pancreatic Lesion",
  gating_modes: [GATING_MODE.VIEW_DEPENDENT, GATING_MODE.PHASE_DEPENDENT],
  organ_policy: "required",
  color: "#db2777", // pink-600
  field_groups: ["General", "Solid Features", "Resectability (NCCN)"],
  fields: {
    lesion_subtype: {
      type: "select",
      display_name: "Lesion Subtype",
      options: [
        "ipmn",
        "mucinous_cystic_neoplasm",
        "serous_cystadenoma",
        "pdac",
        "neuroendocrine_tumor",
        "metastasis",
        "mass_forming_pancreatitis",
        "other",
        "indeterminate",
      ],
      required: false,
      group: "General",
    },
    pancreatic_segment: {
      type: "select",
      display_name: "Pancreatic Segment",
      options: ["head_uncinate", "neck", "body", "tail", "diffuse"],
      required: true,
      group: "General",
      light: true,
    },
    main_pancreatic_duct: {
      type: "select",
      display_name: "Main Pancreatic Duct",
      options: ["normal_leq_3mm", "dilated_3_5mm", "dilated_5_9mm", "dilated_geq_10mm"],
      required: true,
      group: "General",
      light: true,
    },
    vascular_contact: {
      type: "select",
      display_name: "Vascular Contact",
      options: ["none", "abutment_leq_180", "encasement_gt_180", "occlusion_thrombosis"],
      required: false,
      required_when: {
        lesion_subtype: [
          "pdac",
          "neuroendocrine_tumor",
          "metastasis",
          "mass_forming_pancreatitis",
          "other",
          "indeterminate",
        ],
      },
      group: "Solid Features",
    },
    biliary_obstruction: {
      type: "select",
      display_name: "Biliary Obstruction (CBD)",
      options: ["absent", "present"],
      required: false,
      required_when: {
        lesion_subtype: [
          "pdac",
          "neuroendocrine_tumor",
          "metastasis",
          "mass_forming_pancreatitis",
          "other",
          "indeterminate",
        ],
      },
      group: "Solid Features",
    },
    resectability: {
      type: "select",
      display_name: "Resectability (NCCN)",
      options: [
        "resectable",
        "borderline_resectable",
        "unresectable_locally_advanced",
        "metastatic",
      ],
      required: false,
      group: "Resectability (NCCN)",
    },
  },
};

// ============================================================================
// Colon Lesion Schema (cancer-seg, suspected malignancy — clinical review 2026-06-23)
// ============================================================================

export const COLON_LESION_SCHEMA: SegmentationTypeSchema = {
  display_name: "Colon Lesion (suspected malignancy)",
  gating_modes: [GATING_MODE.PHASE_DEPENDENT],
  organ_policy: "required",
  color: "#f97316", // orange-500
  field_groups: ["General", "Local Staging (CT)", "Complications"],
  fields: {
    colon_segment: {
      type: "select",
      display_name: "Colon Segment",
      options: [
        "cecum",
        "ascending_colon",
        "transverse_colon",
        "descending_colon",
        "sigmoid_colon",
        "rectum",
      ],
      required: true,
      group: "General",
      light: true,
    },
    rectal_height: {
      type: "select",
      display_name: "Rectal Height (from anal verge)",
      options: ["low_0_5cm", "mid_5_10cm", "high_10_15cm"],
      required: false,
      required_when: { colon_segment: ["rectum"] },
      group: "General",
      light: true,
    },
    ct_t_category: {
      type: "select",
      display_name: "CT T-Category",
      options: ["t1_t2", "t3", "t4a", "t4b", "indeterminate"],
      required: false,
      group: "Local Staging (CT)",
    },
    extramural_depth: {
      type: "select",
      display_name: "Extramural Depth",
      options: ["leq_5mm", "gt_5mm", "indeterminate"],
      required: false,
      required_when: { ct_t_category: ["t3"] },
      group: "Local Staging (CT)",
    },
    pericolic_fat_stranding: {
      type: "select",
      display_name: "Pericolic Fat Stranding",
      options: ["absent", "present"],
      required: false,
      group: "Local Staging (CT)",
    },
    extramural_venous_invasion: {
      type: "select",
      display_name: "Extramural Venous Invasion (EMVI)",
      options: ["absent", "present", "indeterminate"],
      required: false,
      group: "Local Staging (CT)",
    },
    adjacent_organ_invasion: {
      type: "select",
      display_name: "Adjacent Organ Invasion",
      options: ["absent", "present", "indeterminate"],
      required: false,
      required_when: { ct_t_category: ["t4b"] },
      group: "Local Staging (CT)",
    },
    obstruction: {
      type: "select",
      display_name: "Obstruction",
      options: ["none", "partial", "complete"],
      required: true,
      group: "Complications",
      light: true,
    },
    perforation: {
      type: "select",
      display_name: "Perforation",
      options: ["absent", "contained", "free"],
      required: false,
      group: "Complications",
    },
  },
};

// ============================================================================
// Aorta Schema (segmental measurement — clinical review 2026-06-23)
// ============================================================================

export const AORTA_SCHEMA: SegmentationTypeSchema = {
  display_name: "Aorta (segmental measurement)",
  gating_modes: [GATING_MODE.PHASE_DEPENDENT],
  organ_policy: "none",
  color: "#06b6d4", // cyan-500
  field_groups: ["Segment & Measurement", "Morphology", "Acute / Pathology"],
  fields: {
    aortic_segment: {
      type: "select",
      display_name: "Aortic Segment / Level",
      options: [
        "ascending_aorta",
        "aortic_arch",
        "descending_thoracic",
        "suprarenal_abdominal",
        "infrarenal_abdominal",
      ],
      required: true,
      light: true,
      group: "Segment & Measurement",
    },
    max_outer_diameter_mm: {
      type: "number",
      display_name: "Max Outer Diameter",
      required: true,
      light: true,
      group: "Segment & Measurement",
      unit: "mm",
    },
    morphology: {
      type: "select",
      display_name: "Morphology",
      options: ["normal", "fusiform_aneurysm", "saccular_aneurysm", "pseudoaneurysm"],
      required: false,
      group: "Morphology",
    },
    dissection: {
      type: "select",
      display_name: "Dissection",
      options: ["absent", "present"],
      required: false,
      group: "Acute / Pathology",
    },
    dissection_type: {
      type: "select",
      display_name: "Dissection Type (Stanford)",
      options: ["stanford_a", "stanford_b", "indeterminate"],
      required: false,
      required_when: { dissection: ["present"] },
      group: "Acute / Pathology",
    },
    false_lumen_status: {
      type: "select",
      display_name: "False Lumen Status",
      options: ["patent", "partially_thrombosed", "completely_thrombosed"],
      required: false,
      required_when: { dissection: ["present"] },
      group: "Acute / Pathology",
    },
    intramural_hematoma: {
      type: "select",
      display_name: "Intramural Hematoma",
      options: ["absent", "present"],
      required: false,
      group: "Acute / Pathology",
    },
    penetrating_ulcer: {
      type: "select",
      display_name: "Penetrating Atherosclerotic Ulcer",
      options: ["absent", "present"],
      required: false,
      group: "Acute / Pathology",
    },
    rupture: {
      type: "select",
      display_name: "Rupture",
      options: ["absent", "contained", "active_extravasation"],
      required: false,
      group: "Acute / Pathology",
    },
  },
};

// ============================================================================
// Schema Registry
// ============================================================================

// ============================================================================
// Organ Schema (opt-in per project — whole-organ mask, e.g. the lesion-bearing
// kidney). No annotator-entered attributes: it's a mask, not a finding. The
// segment is pinned to a reserved value on the backend; laterality is derived
// from geometry at export, not entered here.
// ============================================================================

export const ORGAN_SCHEMA: SegmentationTypeSchema = {
  display_name: "Organ",
  gating_modes: [],
  organ_policy: "none",
  color: "#3b82f6", // blue-500 — matches the organ mask overlay; distinct from purple/pink lesions
  fields: {},
};

/** All annotation schemas, keyed by category. Single source of truth for the frontend. */
export const ANNOTATION_SCHEMAS: AnnotationSchemaMap = {
  renal_lesion: RENAL_LESION_SCHEMA,
  renal_stone: RENAL_STONE_SCHEMA,
  // Same reviewed Urinary Stone schema (its locations already span calyces →
  // ureter → UVJ → bladder); distinct display name + mask color. Mirrors the
  // backend registry — keep the two in sync or ureteral Accepts 422.
  obstructing_ureteral_stone: {
    ...RENAL_STONE_SCHEMA,
    display_name: "Obstructing Ureteral Stone",
    color: "#f43f5e", // rose — distinct from renal amber when co-rendered
  },
  hepatic_lesion: HEPATIC_LESION_SCHEMA,
  pancreatic_lesion: PANCREATIC_LESION_SCHEMA,
  colon_lesion: COLON_LESION_SCHEMA,
  aorta: AORTA_SCHEMA,
  renal_cyst: RENAL_CYST_SCHEMA,
  pulmonary_nodule: PULMONARY_NODULE_SCHEMA,
  lymph_node: LYMPH_NODE_SCHEMA,
  organ: ORGAN_SCHEMA,
};

// ============================================================================
// Gating-mode derivation (mirrors backend `_derive_gating_mode`)
// ============================================================================

/**
 * Fixed precedence for resolving a project's default viewport mode when its
 * assigned categories disagree. Must match the backend
 * `_GATING_MODE_PRECEDENCE` in `labeling_endpoints.py`.
 */
const GATING_MODE_PRECEDENCE: readonly GatingMode[] = [
  GATING_MODE.VIEW_DEPENDENT,
  GATING_MODE.PHASE_DEPENDENT,
  GATING_MODE.SLICE_DEPENDENT,
] as const;

/**
 * Distinct non-"none" gating modes contributed by the assigned categories,
 * returned in precedence order. Unknown categories and `none`-mode categories
 * (e.g. `organ`) contribute nothing. An empty result means a hybrid/legacy
 * project with no segmentation-driving category → caller should fall to "none".
 */
export function availableGatingModes(labelsToAssign: readonly string[]): GatingMode[] {
  const present = new Set<GatingMode>();
  for (const category of labelsToAssign) {
    for (const mode of ANNOTATION_SCHEMAS[category]?.gating_modes ?? []) {
      if (mode !== GATING_MODE.NONE) present.add(mode);
    }
  }
  return GATING_MODE_PRECEDENCE.filter((mode) => present.has(mode));
}

/**
 * The default active mode for a set of available modes: the first by fixed
 * precedence (view > phase > slice), or "none" if the set is empty. Used to
 * seed the active mode on case open; for hybrids the user can override via the
 * view-mode selector (it never auto-changes after seeding).
 */
export function defaultGatingMode(modes: readonly GatingMode[]): GatingMode {
  for (const mode of GATING_MODE_PRECEDENCE) {
    if (modes.includes(mode)) return mode;
  }
  return GATING_MODE.NONE;
}

// ============================================================================
// Organ-policy derivation (mirrors backend organ_policy)
// ============================================================================

/**
 * Whether the "Segment Organ" button should be offered for a case, given its
 * assigned categories (C2). True when any assigned category's organ_policy is
 * "required" or "optional". Legacy projects that list "organ" directly in
 * labels_to_assign are also honored.
 */
export function organEnabledForLabels(labelsToAssign: readonly string[]): boolean {
  if (labelsToAssign.includes("organ")) return true;
  return labelsToAssign.some((category) => {
    const policy = ANNOTATION_SCHEMAS[category]?.organ_policy;
    return policy !== undefined && policy !== "none";
  });
}

/**
 * Whether an organ mask is still required before the case can be completed (C3),
 * given the categories of the currently-saved annotations. True once ≥1 saved
 * lesion belongs to an organ_policy="required" category and no organ mask is
 * saved yet. `optional`/`none` categories never gate.
 */
export function organGateActiveForSaved(savedCategories: readonly string[]): boolean {
  const organSaved = savedCategories.includes("organ");
  const organRequired = savedCategories.some(
    (category) => ANNOTATION_SCHEMAS[category]?.organ_policy === "required",
  );
  return organRequired && !organSaved;
}

/**
 * Resolve the schema for an actively-selected category. The annotation panel
 * gates the Add-Annotation picker to the project's labels_to_assign (`gated`),
 * but some categories — notably `organ` — are offered via the dedicated Segment
 * Organ button (per `organ_policy`) and are intentionally absent from that
 * picker. Fall back to the full registry so the form (and Save) render for those
 * categories instead of "category not available in this project's schema".
 * Returns null only for categories absent from BOTH maps (truly unknown).
 */
export function resolveAnnotationSchema(
  gated: AnnotationSchemaMap,
  category: string,
): SegmentationTypeSchema | null {
  return gated[category] ?? ANNOTATION_SCHEMAS[category] ?? null;
}

const DISPLAY_OVERRIDES: Record<string, string> = {
  // ── Shared ──
  none: "None",
  present: "Present",
  indeterminate: "Indeterminate",
  absent: "Absent",
  irregular: "Irregular",
  yes: "Yes",
  no: "No",
  other: "Other",
  left: "Patient Left",
  right: "Patient Right",
  // ── Renal lesion — general ──
  cystic: "Cystic",
  solid: "Solid",
  mixed: "Mixed",
  upper_pole: "Upper pole",
  mid_kidney: "Mid kidney",
  lower_pole: "Lower pole",
  // ── Renal lesion — solid features ──
  exophytic: "Exophytic",
  intrarenal: "Intrarenal",
  well_defined: "Well-defined",
  infiltrative: "Infiltrative",
  homogeneous: "Homogeneous",
  heterogeneous: "Heterogeneous",
  hyperenhancing: "Hyperenhancing",
  isoenhancing: "Isoenhancing",
  hypoenhancing: "Hypoenhancing",
  less_25pct: "<25%",
  "25_50pct": "25–50%",
  greater_50pct: ">50%",
  peripheral: "Peripheral",
  coarse: "Coarse",
  rcc: "RCC",
  oncocytoma: "Oncocytoma",
  aml: "AML",
  // ── Renal cyst (Bosniak) ──
  thin_leq_2mm: "Thin (≤2mm)",
  minimally_thickened_3mm: "Minimally thickened (3mm)",
  thickened_geq_4mm: "Thickened (≥4mm)",
  few_thin_1_3: "Few thin (1–3)",
  many_thin_geq_4: "Many thin (≥4)",
  thickened_irregular_geq_4mm: "Thickened/irregular (≥4mm)",
  present_obtuse_margins: "Present (obtuse margins)",
  present_acute_margins: "Present (acute margins)",
  // ── Renal stone ──
  upper_pole_calyx: "Upper pole calyx",
  interpolar_calyx: "Interpolar calyx",
  lower_pole_calyx: "Lower pole calyx",
  renal_pelvis: "Renal pelvis",
  upj: "UPJ",
  proximal_ureter: "Proximal ureter",
  mid_ureter: "Mid ureter",
  distal_ureter: "Distal ureter",
  uvj: "UVJ",
  bladder: "Bladder",
  tiny_lt_3mm: "<3mm",
  small_3_5mm: "3–5mm",
  medium_5_10mm: "5–10mm",
  large_10_20mm: "10–20mm",
  very_large_gt_20mm: ">20mm",
  mild_hydronephrosis: "Mild hydronephrosis",
  moderate_hydronephrosis: "Moderate hydronephrosis",
  severe_hydronephrosis: "Severe hydronephrosis",
  low_lt_500: "<500 HU",
  medium_500_1000: "500–1000 HU",
  high_gt_1000: ">1000 HU",
  round_oval: "Round/oval",
  staghorn: "Staghorn",
  single: "Single",
  few_2_3: "2–3",
  multiple_gt_3: ">3",
  // ── Hepatic lesion — subtypes ──
  cyst: "Cyst",
  hemangioma: "Hemangioma",
  focal_nodular_hyperplasia: "Focal nodular hyperplasia (FNH)",
  hepatic_adenoma: "Hepatic adenoma",
  hcc: "HCC",
  metastasis: "Metastasis",
  cholangiocarcinoma: "Cholangiocarcinoma",
  // ── Hepatic lesion — cyst features ──
  thin_imperceptible: "Thin/imperceptible",
  simple_0_20: "Simple (0–20 HU)",
  hyperdense_gt_20: "Hyperdense (>20 HU)",
  wall_enhancement: "Wall enhancement",
  septal_enhancement: "Septal enhancement",
  nodular_enhancement: "Nodular enhancement",
  // ── Hepatic lesion — CT enhancement ──
  phase_unavailable: "Phase unavailable",
  flash_filling: "Flash filling",
  peripheral_discontinuous_nodular: "Peripheral discontinuous nodular",
  rim_continuous: "Rim continuous",
  progressive_fill_in: "Progressive fill-in",
  isointense_equilibration: "Isointense (equilibration)",
  washout: "Washout",
  persistent_enhancement: "Persistent enhancement",
  complete_fill_in: "Complete fill-in",
  incomplete_fill_in: "Incomplete fill-in",
  isointense: "Isointense",
  progressive_central_enhancement: "Progressive central enhancement",
  // ── Hepatic lesion — LI-RADS ──
  LR_1: "LR-1 (Definitely benign)",
  LR_2: "LR-2 (Probably benign)",
  LR_3: "LR-3 (Intermediate)",
  LR_4: "LR-4 (Probably HCC)",
  LR_5: "LR-5 (Definitely HCC)",
  LR_M: "LR-M (Probably malignant, not HCC)",
  LR_TIV: "LR-TIV (Tumor in vein)",
  not_applicable: "Not applicable",
  // ── Hepatic lesion — morphologic/malignant ──
  tumor_in_vein: "Tumor in vein",
  fat_containing: "Fat-containing",
  hemorrhagic: "Hemorrhagic",
  segment_1: "Segment 1 (Caudate)",
  segment_2: "Segment 2",
  segment_3: "Segment 3",
  segment_4a: "Segment 4A",
  segment_4b: "Segment 4B",
  segment_5: "Segment 5",
  segment_6: "Segment 6",
  segment_7: "Segment 7",
  segment_8: "Segment 8",
  // ── Pulmonary nodule ──
  part_solid: "Part-solid",
  ground_glass: "Ground glass",
  smooth: "Smooth",
  lobulated: "Lobulated",
  spiculated: "Spiculated",
  ill_defined: "Ill-defined",
  benign_pattern: "Benign pattern",
  right_upper_lobe: "Right upper lobe",
  right_middle_lobe: "Right middle lobe",
  right_lower_lobe: "Right lower lobe",
  left_upper_lobe: "Left upper lobe",
  lingula: "Lingula",
  left_lower_lobe: "Left lower lobe",
  subpleural: "Subpleural",
  perifissural: "Perifissural",
  central: "Central",
  peribronchial: "Peribronchial",
  // ── Lymph node ──
  oval: "Oval",
  round: "Round",
  conglomerate: "Conglomerate",
  cervical: "Cervical",
  mediastinal: "Mediastinal",
  pulmonary_hilar: "Pulmonary hilar",
  axillary: "Axillary",
  celiac: "Celiac",
  porta_hepatis: "Porta hepatis",
  gastrohepatic: "Gastrohepatic",
  peripancreatic: "Peripancreatic",
  mesenteric: "Mesenteric",
  paraaortic: "Para-aortic",
  aortocaval: "Aortocaval",
  paracaval: "Paracaval",
  retrocrural: "Retrocrural",
  renal_hilum: "Renal hilum",
  common_iliac: "Common iliac",
  external_iliac: "External iliac",
  internal_iliac: "Internal iliac",
  obturator: "Obturator",
  presacral: "Presacral",
  mesorectal: "Mesorectal",
  inguinal: "Inguinal",
  midline: "Midline",
  bilateral: "Bilateral",
  // ── Pancreatic lesion ──
  ipmn: "IPMN",
  mucinous_cystic_neoplasm: "Mucinous cystic neoplasm (MCN)",
  serous_cystadenoma: "Serous cystadenoma",
  pdac: "PDAC",
  neuroendocrine_tumor: "Neuroendocrine tumor",
  mass_forming_pancreatitis: "Mass-forming pancreatitis",
  head_uncinate: "Head / uncinate",
  diffuse: "Diffuse",
  normal_leq_3mm: "Normal (≤3 mm)",
  dilated_3_5mm: "Dilated (3–5mm)",
  dilated_5_9mm: "Dilated (5–9mm)",
  dilated_geq_10mm: "Dilated (≥10mm)",
  abutment_leq_180: "Abutment (≤180°)",
  encasement_gt_180: "Encasement (>180°)",
  occlusion_thrombosis: "Occlusion / thrombosis",
  resectable: "Resectable",
  borderline_resectable: "Borderline resectable",
  unresectable_locally_advanced: "Unresectable (locally advanced)",
  metastatic: "Metastatic",
  // ── Colon lesion ──
  ascending_colon: "Ascending colon",
  hepatic_flexure: "Hepatic flexure",
  transverse_colon: "Transverse colon",
  splenic_flexure: "Splenic flexure",
  descending_colon: "Descending colon",
  sigmoid_colon: "Sigmoid colon",
  rectosigmoid: "Rectosigmoid",
  rectum: "Rectum",
  low_0_5cm: "Low (0–5 cm)",
  mid_5_10cm: "Mid (5–10 cm)",
  high_10_15cm: "High (10–15 cm)",
  t1_t2: "T1–T2 (confined to wall)",
  t3: "T3 (beyond muscularis)",
  t4a: "T4a (visceral peritoneum)",
  t4b: "T4b (adjacent organ)",
  leq_5mm: "≤5 mm",
  gt_5mm: ">5 mm",
  partial: "Partial",
  complete: "Complete",
  contained: "Contained",
  free: "Free",
  // ── Aorta ──
  ascending_aorta: "Ascending aorta (root → ascending)",
  aortic_arch: "Aortic arch",
  descending_thoracic: "Descending thoracic aorta",
  suprarenal_abdominal: "Suprarenal abdominal",
  infrarenal_abdominal: "Infrarenal abdominal (incl. bifurcation)",
  fusiform_aneurysm: "Fusiform aneurysm",
  saccular_aneurysm: "Saccular aneurysm",
  pseudoaneurysm: "Pseudoaneurysm",
  stanford_a: "Stanford A",
  stanford_b: "Stanford B",
  patent: "Patent",
  partially_thrombosed: "Partially thrombosed",
  completely_thrombosed: "Completely thrombosed",
  active_extravasation: "Active extravasation",
};

/**
 * Format a snake_case option value for display.
 * e.g., "thin_leq_2mm" → "Thin ≤2mm"
 */
export function formatOptionLabel(value: string): string {
  return (
    DISPLAY_OVERRIDES[value] ?? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
